import { useParams } from 'react-router';

import { useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';

import { useI18n } from '@app-velocity/i18n';

import { MessageBus } from '@app-velocity/message-bus';

import {
  TCreatePrototypeDto,
  TPrototypePageDto,
  TPrototypePartialDto,
} from '@app-velocity/av-api-v2';

import {
  AdditionalDataProperty,
  ComponentVariantsToSave,
  ErrorWithNodeData,
  GlobalNodeMap,
  NodeMap,
  PageActionToSave,
} from '@app-velocity/common';

import { SimpleTextNode } from '../../../../../../../core/types';

import { useConfigureScenarioStore } from '../../../../../../store/useConfigureScenarioStore';
import { useFlows } from '../../../../../../hooks';

import {
  clearTemp,
  getFlowPageName,
  getVariantNodeImageFromTempLayer,
  loadPage,
} from '../../../../../../../core';
import { bufferToBase64 } from '../../../../../../utils/image';
import { useConfigureScenarioMutations } from './useConfigureAuthScenario';
import { isChallengeSaveError } from '../../../../../../../guards/isChallengeSaveError';
import { useGeneratePage } from './useGeneratePage';
import { TrackedPromise } from '../utils/tracked-promise';
import { usePrototypeWhitelist } from '../../../../../../hooks/usePrototypeWhitelist';

const messageBus = new MessageBus('system');
// const prototypesMessageBus = new MessageBus('prototypes');

type StartingNode = {
  nodeId: string;
  parentNodeId: string;
};

type PartialToSave = {
  type: 'variant' | 'overlay' | 'dynamic';
  fileName: string;
  name: string;
  index: string;
  image: string;
  data: string;
};

type BufferedVariant = {
  id: string;
  parentId: string;
  nodeId?: string;
  partialId?: string;
  name?: string;
  pageId: string;
  instanceId: string;
  actions: PageActionToSave[];
  properties: AdditionalDataProperty[];
  visibleWhen?: { [variable: string]: boolean | string | number }[];
  default?: boolean;
  x: number;
  y: number;
};

export const useConfigureScenario = () => {
  const { t } = useI18n();
  const setIsConfiguring = useConfigureScenarioStore(
    (store) => store.setIsConfiguring
  );

  const { getMatchedDemoName } = usePrototypeWhitelist();

  const promises = useRef<
    TrackedPromise<
      | TPrototypePageDto
      | TPrototypePartialDto
      | TPrototypePartialDto[]
      | undefined
    >[]
  >([]);

  const partialsToSave = useRef<PartialToSave[]>([]);

  const { nodeId } = useParams<{
    nodeId: string;
  }>();
  const [isScenarioContentCreating, setScenarioContentCreating] =
    useState(false);
  const [isScenarioSaved, setIsScenarioSaved] = useState(false);
  const [saveError, setSaveError] = useState<
    AxiosError | Error | ErrorWithNodeData | null
  >(null);
  const [pagesCount, setPagesCount] = useState<number>(0);
  const [partialsCount, setPartialsCount] = useState<number>(0);
  const [savedPagesCount, setSavedPagesCount] = useState<number>(0);
  const [savedPartialsCount, setSavedPartialsCount] = useState<number>(0);
  const [finishedCount, setFinishedCount] = useState<number>(0);
  const [totalToFinish, setTotalTofinish] = useState<number>(0);

  const { getAllNodeIdsToGenerate } = useFlows();
  const { generatePage, generatePartial, generateVariant } = useGeneratePage();

  const [abortController, setAbortController] = useState<AbortController>(
    new AbortController()
  );

  const {
    publishScenarioMutation,
    createScenarioMutation,
    createPageMutation,
    createBatchPartialsMutation,
    createPartialMutation,
  } = useConfigureScenarioMutations(abortController);

  const {
    mutateAsync: saveScenario,
    data: createdScenario,
    isPending: isBaseScenarioPending,
  } = createScenarioMutation;

  const { mutateAsync: publishScenario } = publishScenarioMutation;

  const { mutateAsync: savePage, isPending: isPagePending } =
    createPageMutation;

  const { mutateAsync: saveBatchPartials, isPending: isPartialsBatchPending } =
    createBatchPartialsMutation;

  const { mutateAsync: savePartial, isPending: isPartialPending } =
    createPartialMutation;

  useEffect(() => {
    clearTemp();
  }, []);

  const savePartialsBatch = (prototypeId: string) => {
    // if there is single partial to be saved

    if (partialsToSave.current.length === 1) {
      const promise = savePartial({
        partial: partialsToSave.current[0],
        prototypeId,
      })
        .then((result) => {
          setFinishedCount((prev) => prev + 1);

          return result;
        })
        .catch((error) => {
          // there is no need to rethrow already handled issue
          if ((error as AxiosError).code !== 'ERR_CANCELED') {
            clearProcess();
            setSaveError(
              new Error('An error encoured while trying to save challenge')
            );
          }

          throw error;
        });

      promises.current.push(new TrackedPromise(promise));
      setTotalTofinish((prev) => prev + 1);
      partialsToSave.current = [];
    } else if (partialsToSave.current.length > 1) {
      const amountToSave = partialsToSave.current.length;
      const promise = saveBatchPartials({
        partialsBatch: partialsToSave.current,
        prototypeId,
      })
        .then((result) => {
          setFinishedCount((prev) => prev + amountToSave);

          return result;
        })
        .catch((error) => {
          // there is no need to rethrow already handled issue
          if ((error as AxiosError).code !== 'ERR_CANCELED') {
            clearProcess();
            setSaveError(
              new Error('An error encoured while trying to save challenge')
            );
          }

          throw error;
        });
      promises.current.push(new TrackedPromise(promise));
      setTotalTofinish((prev) => prev + amountToSave);
      partialsToSave.current = [];
    }
  };

  // aproximating screen density using smallest font size found in the document
  const approximateScreenDensity = (textNodes: SimpleTextNode[]): number => {
    if (textNodes.length === 0) return 1;

    const fontSizes = textNodes.map((t) => t.size);

    const sortedSizes = fontSizes.sort((a, b) => a - b);

    const mid = Math.floor(sortedSizes.length / 2);
    const median =
      sortedSizes.length % 2 === 0
        ? (sortedSizes[mid - 1] + sortedSizes[mid]) / 2
        : sortedSizes[mid];

    if (median > 36) return 3;
    if (median > 24) return 2;

    return 1;
  };

  const saveEverything = async (
    prototypeId: string,
    startingNode: StartingNode,
    initialPageIds: string[],
    initialPartialIds: string[],
    initialDynamicNodePointsIds: string[],
    variants: ComponentVariantsToSave[],
    nodesMap: GlobalNodeMap,
    clonedIdsMap: { [clonedId: string]: string },
    originalIdsMap: { [originalId: string]: string },
    globalTextNodes: SimpleTextNode[]
  ) => {
    const pageIds = [...initialPageIds];
    const partialIds = [...initialPartialIds];
    const dynamicNodePointsIds = [...initialDynamicNodePointsIds];

    const savedPageIds = [];
    const savedPartialIds = [];
    const savedDynamicNodeIds = [];

    const approximatedScreenDensity = approximateScreenDensity(globalTextNodes);

    setSaveError(null);

    while (
      pageIds.length > 0 ||
      partialIds.length > 0 ||
      dynamicNodePointsIds.length > 0
    ) {
      const pageIdToSave = pageIds.shift();

      if (pageIdToSave) {
        await saveSinglePage(
          pageIdToSave,
          savedPageIds.length,
          prototypeId,
          startingNode,
          nodesMap[pageIdToSave.replace(/[:;]/g, '-')],
          clonedIdsMap,
          originalIdsMap,
          approximatedScreenDensity
        );

        savedPageIds.push(pageIdToSave);

        setSavedPagesCount((prev) => prev + 1);
      }

      const partialIdToSave = partialIds.shift();

      if (partialIdToSave) {
        await saveSinglePartial(
          partialIdToSave,
          savedPartialIds.length,
          prototypeId,
          startingNode,
          nodesMap[partialIdToSave.replace(/[:;]/g, '-')],
          clonedIdsMap,
          originalIdsMap
        );
        savedPartialIds.push(partialIdToSave);
        setSavedPartialsCount((prev) => prev + 1);
      }

      const dynamicNodeIdToSave = dynamicNodePointsIds.shift();

      // @TODO should be possible to use just partials for that
      if (dynamicNodeIdToSave) {
        await saveSingleDynamicNodePartial(
          dynamicNodeIdToSave,
          savedDynamicNodeIds.length,
          prototypeId,
          startingNode,
          nodesMap[dynamicNodeIdToSave.replace(/[:;]/g, '-')]
        );
        savedDynamicNodeIds.push(dynamicNodeIdToSave);
        setSavedPartialsCount((prev) => prev + 1);
      }
    }

    await saveAllVariants(variants, prototypeId, clonedIdsMap, originalIdsMap);

    savePartialsBatch(prototypeId);
  };

  const saveVariantsBuffer = async (
    variantsBuffer: BufferedVariant[],
    prototypeId: string,
    startIndex: number,
    clonedIdsMap: { [clonedId: string]: string },
    originalIdsMap: { [originalId: string]: string }
  ) => {
    const variantsNames = variantsBuffer.map((variantFromBuffer) => {
      const {
        id,
        default: isDefault,
        parentId,
        instanceId,
      } = variantFromBuffer;

      return `${parentId}_${instanceId}_${id}${isDefault ? '_default' : ''}`;
    });
    const variantImagesResponse = await getVariantNodeImageFromTempLayer(
      variantsNames
    );

    if (!Array.isArray(variantImagesResponse)) {
      throw new Error('NODE_NOT_FOUND');
    }

    let variantFromBufferIndex = 0;

    for (const variantToSave of variantsBuffer) {
      const {
        id,
        nodeId,
        pageId,
        partialId,
        default: isDefault,
        actions,
        properties,
        instanceId,
        visibleWhen,
        x,
        y,
        name,
        parentId,
      } = variantToSave;

      const variantImageResponse =
        variantImagesResponse[variantFromBufferIndex];

      if (!('converted' in variantImageResponse)) {
        throw new Error('NODE_NOT_FOUND');
      }

      const variantImage = variantImageResponse.converted
        ? await bufferToBase64(variantImageResponse.converted)
        : '';

      variantImageResponse.converted = null;

      partialsToSave.current.push({
        type: 'variant',
        fileName: `${instanceId}_${id}`,
        name: `${instanceId}_${id}`,
        index: (startIndex + variantFromBufferIndex).toString(),
        image: variantImage,
        data: JSON.stringify({
          name,
          id: originalIdsMap[nodeId!] || nodeId,
          partialId,
          visibleInParent: parentId,
          visibleIn: pageId,
          visibleWhen,
          actions,
          properties,
          default: isDefault,
          x,
          y,
        }),
      });

      variantFromBufferIndex++;
    }

    if (partialsToSave.current.length >= 10) {
      savePartialsBatch(prototypeId);
    }
  };

  const saveAllVariants = async (
    componentVariants: ComponentVariantsToSave[],
    prototypeId: string,
    clonedIdsMap: { [clonedId: string]: string },
    originalIdsMap: { [originalId: string]: string }
  ) => {
    let index = 0;
    let variantsBufferToSave: BufferedVariant[] = [];

    for (const componentVariant of componentVariants) {
      const { variants, parentId, instanceId, pageId, partialId } =
        componentVariant;

      for (const variant of variants) {
        variantsBufferToSave.push({
          ...variant,
          x: componentVariant.x || 0,
          y: componentVariant.y || 0,
          instanceId,
          parentId,
          partialId,
          pageId,
        });

        if (variantsBufferToSave.length >= 10) {
          await saveVariantsBuffer(
            variantsBufferToSave,
            prototypeId,
            index,
            clonedIdsMap,
            originalIdsMap
          );
          variantsBufferToSave = [];
        }

        index++;
      }
    }

    if (variantsBufferToSave.length > 0) {
      await saveVariantsBuffer(
        variantsBufferToSave,
        prototypeId,
        index,
        clonedIdsMap,
        originalIdsMap
      );

      variantsBufferToSave = [];
    }
  };

  const saveSinglePage = async (
    IDToGenerate: string,
    index: number,
    prototypeId: string,
    startingNode: StartingNode,
    nodesMap: NodeMap,
    clonedIdsMap: { [clonedId: string]: string },
    originalIdsMap: { [originalId: string]: string },
    approximatedScreenDensity: number
  ) => {
    const response = await generatePage(IDToGenerate, nodesMap);

    if (isChallengeSaveError(response)) {
      throw new ErrorWithNodeData(
        response.type,
        response.nodeId,
        response.nodeName
      );
    }

    const { image, data: pageData } = response;

    let pageImage = image.converted;

    const pageNode = await getFlowPageName(IDToGenerate);

    const originalID = clonedIdsMap[IDToGenerate];

    if (pageNode) {
      const promise = savePage(
        {
          page: {
            fileName: originalID,
            name: pageNode?.name || originalID,
            index: index.toString(),
            image: pageImage,
            data: JSON.stringify({
              ...pageData,
              density: approximatedScreenDensity,
            }),
          },
          prototypeId,
        },
        {}
      )
        .then((result) => {
          setFinishedCount((prev) => prev + 1);

          return result;
        })
        .catch((error) => {
          // there is no need to rethrow already handled issue
          if ((error as AxiosError).code !== 'ERR_CANCELED') {
            clearProcess();
            if (error.response?.data?.code === 6003) {
              setSaveError(
                new Error('Maximum allowed frame size is 3000x20000px')
              );
            } else
              setSaveError(
                new Error('An error encoured while trying to save challenge')
              );
          }

          throw error;
        });
      promises.current.push(new TrackedPromise(promise));
      setTotalTofinish((prev) => prev + 1);

      // forcing heap to clear
      pageImage = '';
    }

    return true;
  };

  const saveSinglePartial = async (
    IDToGenerate: string,
    index: number,
    prototypeId: string,
    startingNode: StartingNode,
    nodesMap: NodeMap,
    clonedIdsMap: { [clonedId: string]: string },
    originalIdsMap: { [originalId: string]: string }
  ) => {
    const response = await generatePartial(IDToGenerate, nodesMap);

    if (isChallengeSaveError(response)) {
      throw new ErrorWithNodeData(
        response.type,
        response.nodeId,
        response.nodeName
      );
    }

    const { image, data: pageData } = response;

    let pageImage = image.converted;

    const originalID = clonedIdsMap[IDToGenerate];

    if (pageImage) {
      partialsToSave.current.push({
        type: 'overlay',
        fileName: originalID,
        name: originalID,
        index: index.toString(),
        image: pageImage,
        data: JSON.stringify({
          ...pageData,
        }),
      });

      if (partialsToSave.current.length >= 10) {
        savePartialsBatch(prototypeId);
      }

      setSavedPartialsCount((prev) => prev + 1);
    }

    // forcing heap to clear
    pageImage = '';

    return true;
  };

  const saveSingleDynamicNodePartial = async (
    IDToGenerate: string,
    index: number,
    prototypeId: string,
    startingNode: StartingNode,
    nodesMap: NodeMap
  ) => {
    const response = await generateVariant(
      IDToGenerate,
      startingNode?.parentNodeId || '',
      nodesMap
    );

    if (isChallengeSaveError(response)) {
      throw new ErrorWithNodeData(
        response.type,
        response.nodeId,
        response.nodeName
      );
    }

    const { image, data: pageData } = response;

    let pageImage = image.converted;

    if (pageImage) {
      partialsToSave.current.push({
        type: 'dynamic',
        fileName: IDToGenerate,
        name: IDToGenerate,
        index: index.toString(),
        image: pageImage,
        data: JSON.stringify({
          ...pageData,
        }),
      });

      if (partialsToSave.current.length >= 10) {
        savePartialsBatch(prototypeId);
      }

      setSavedPartialsCount((prev) => prev + 1);
    }

    // forcing heap to clear
    pageImage = '';

    return true;
  };

  const saveFromDemo = async (
    data: TCreatePrototypeDto,
    dontSkipVariants = false
  ) => {
    setIsConfiguring(true);

    // if prototype fails to create from demo it should be created from scratch
    try {
      const result = await saveScenario({ ...data });

      if (!result || !result.id) return;

      const publishedScenario = await publishScenario(result.id);
      finishScenarioCreating();
      setIsScenarioSaved(true);

      return publishedScenario;
    } catch {
      return await saveFromScratch(
        { ...data, forceNew: true },
        dontSkipVariants
      );
    }
  };

  const saveFromScratch = async (
    data: TCreatePrototypeDto,
    dontSkipVariants = false
  ) => {
    if (!nodeId)
      throw new Error(t('prototypes:save_error.unable_to_find_starting_node'));

    await loadPage();

    const nodesResult = await getAllNodeIdsToGenerate(nodeId, dontSkipVariants);

    if (!nodesResult) return;

    const {
      generatedPoints,
      generatedPartialPoints,
      generatedDynamicNodePoints,
      startingNode,
      variants,
      globalNodeMap,
      // globalWarnings,
      clonedIdsMap,
      originalIdsMap,
      globalTextNodes,
    } = nodesResult;

    if (!startingNode)
      throw new Error(t('prototypes:save_error.unable_to_find_starting_node'));

    const totalVariantsCount = variants.reduce(
      (acc, curr) => curr.variants.length + acc,
      0
    );

    setIsConfiguring(true);
    setPagesCount(generatedPoints.length);
    setPartialsCount(
      generatedPartialPoints.length +
        generatedDynamicNodePoints.length +
        totalVariantsCount
    );

    setScenarioContentCreating(true);

    const result = await saveScenario({ ...data });

    if (!result || !result.id || !startingNode) return;

    if (!result.demo) {
      await saveEverything(
        result.id,
        startingNode,
        generatedPoints,
        generatedPartialPoints,
        generatedDynamicNodePoints,
        variants,
        globalNodeMap,
        clonedIdsMap,
        originalIdsMap,
        globalTextNodes
      );

      await waitForAllToSave(promises.current);
    }

    const publishedScenario = await publishScenario(result.id);
    finishScenarioCreating();
    setIsScenarioSaved(true);

    // @TODO - the modal should be hidden for now - waiting for better design
    // if (globalWarnings && globalWarnings.length > 0) {
    //   prototypesMessageBus.publish('modal:snapshot-warnings:open', {
    //     warnings: globalWarnings || [],
    //   });
    // }

    clearTemp();

    await new Promise((r) => setTimeout(r, 0));

    return publishedScenario;
  };

  const saveScenarioWithPages = async (
    data: TCreatePrototypeDto,
    dontSkipVariants = false
  ) => {
    try {
      setSaveError(null);
      const { fileName } = data;

      const matchedDemoName = getMatchedDemoName(fileName);

      if (matchedDemoName) {
        return await saveFromDemo(data);
      }

      return await saveFromScratch(
        { ...data, forceNew: true },
        dontSkipVariants
      );
    } catch (e: unknown) {
      setAbortController(new AbortController());
      clearTemp();

      // don't catch errors from AbortController
      if ((e as AxiosError).code !== 'ERR_CANCELED') {
        Sentry.captureException(e);
        if (!saveError)
          setSaveError(e as AxiosError | Error | ErrorWithNodeData);
      }

      finishScenarioCreating();
    }
  };

  useEffect(() => {
    if (!isScenarioSaved || isScenarioContentCreating) return;

    if (createdScenario && createdScenario.shareToken) {
      messageBus.publish('notification', {
        title: 'prototypes:scenario_created',
        message:
          savedPagesCount > 0
            ? t('prototypes:scenario_created_description', {
                count: savedPagesCount,
              })
            : undefined,
      });
    }
  }, [isScenarioSaved, createdScenario, isScenarioContentCreating]);

  const waitForAllToSave = async (
    allPromises: TrackedPromise<
      | TPrototypePageDto
      | TPrototypePartialDto
      | TPrototypePartialDto[]
      | undefined
    >[]
  ) => {
    const promise = new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        const finished = allPromises.filter(
          (promise) => promise.status === 'resolved'
        );
        const aborted = allPromises.find(
          (promise) => promise.status === 'rejected'
        );

        if (aborted) {
          clearInterval(interval);
          reject('The process has been aborted');
        }

        if (finished.length === allPromises.length) {
          clearInterval(interval);
          resolve(true);
        }
      }, 500);
    });

    promises.current = [];

    return promise;
  };

  const finishScenarioCreating = () => {
    setIsConfiguring(false);
    setScenarioContentCreating(false);
  };

  const clearProcess = () => {
    setFinishedCount(0);
    setIsConfiguring(false);
    setIsScenarioSaved(false);
    setPagesCount(0);
    setPartialsCount(0);
    setSavedPagesCount(0);
    setSavedPartialsCount(0);
    setScenarioContentCreating(false);
    setTotalTofinish(0);
    clearTemp();
    promises.current = [];
    partialsToSave.current = [];

    if (!abortController.signal.aborted) {
      abortController.abort();
    }
  };

  const abortSaving = () => {
    clearProcess();
    if (!saveError) setSaveError(new Error('The process has been aborted'));
  };

  return {
    saveScenarioWithPages,
    abortSaving,
    saveError,
    createdScenario,
    isScenarioContentCreating,
    isBaseScenarioPending,
    isPagePending: isPartialsBatchPending || isPagePending || isPartialPending,
    pagesCount,
    savedPagesCount,
    partialsCount,
    savedPartialsCount,
    finishedCount,
    totalToFinish,
    // if last page and partial is saved successfully - everything is
    isScenarioSaved,
  };
};
